import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { profileButtonSx } from "./ProfileSection";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { useUpdateUser, useChangePassword } from "../../Query/useUsers";
import color from "../../Constants/colors";
import { ComoInput } from "../Common/ComoInput";

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  userData: {
    name: string;
    surname: string;
    email: string;
    is_sso: boolean;
  };
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onClose,
  userData,
}) => {
  const { t } = useTranslation("profile");

  const [formValues, setFormValues] = useState({
    name: userData.name,
    surname: userData.surname,
    email: userData.email,
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const { mutate: updateUserMutate, isPending: isUpdating } = useUpdateUser();
  const { mutate: changePasswordMutate, isPending: isChangingPassword } =
    useChangePassword();
  const loading = isUpdating || isChangingPassword;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newValue = name === "email" ? value.toLowerCase() : value;

    setFormValues({ ...formValues, [name]: newValue });
  };

  const handleSaveChanges = () => {
    const normalizedEmail = formValues.email.toLowerCase();
    if (
      formValues.oldPassword ||
      formValues.newPassword ||
      formValues.confirmNewPassword
    ) {
      if (formValues.newPassword !== formValues.confirmNewPassword) {
        enqueueSnackbar(
          t("profile.passwordsDoNotMatch", "Passwords do not match."),
          { variant: "warning" },
        );
        return;
      }

      changePasswordMutate(
        {
          currentPassword: formValues.oldPassword,
          newPassword: formValues.newPassword,
        },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    } else {
      const params: { name: string; surname: string; email?: string } = {
        name: formValues.name,
        surname: formValues.surname,
      };
      if (!userData.is_sso) params.email = normalizedEmail;

      updateUserMutate(params, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  const sectionLabel = (text: string) => (
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: color.gray500,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="edit-profile-dialog"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", maxWidth: 540 } }}
    >
      <DialogTitle
        id="edit-profile-dialog"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pt: 2.5,
          pb: 1,
          px: 3,
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontSize: 20,
            fontWeight: 700,
            color: color.primary_dark,
          }}
        >
          {t("profile.editProfileTitle")}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={loading}
          aria-label={t("profile.close")}
          sx={{ color: color.gray500 }}
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {sectionLabel(t("profile.personalInformation"))}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <ComoInput
              id="name"
              label={t("profile.firstName")}
              name="name"
              value={formValues.name}
              onChange={handleInputChange}
              disabled={loading}
            />
            <ComoInput
              id="surname"
              label={t("profile.lastName")}
              name="surname"
              value={formValues.surname}
              onChange={handleInputChange}
              disabled={loading}
            />
          </Box>
          <ComoInput
            id="email"
            label={t("profile.emailAddress")}
            name="email"
            type="email"
            value={formValues.email}
            onChange={handleInputChange}
            disabled={userData.is_sso || loading}
            helperText={
              userData.is_sso ? t("profile.ssoEmailTooltip") : undefined
            }
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleSaveChanges}
              disabled={loading}
              sx={profileButtonSx("primary")}
            >
              {t("profile.saveChanges")}
            </Button>
          </Box>

          {!userData.is_sso && (
            <>
              <Divider sx={{ my: 1, borderColor: color.gray200 }} />
              {sectionLabel(t("profile.changePassword"))}
              <ComoInput
                id="old-password"
                label={t("profile.oldPassword")}
                name="oldPassword"
                type="password"
                autoComplete="current-password"
                value={formValues.oldPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <ComoInput
                id="new-password"
                label={t("profile.newPassword")}
                name="newPassword"
                type="password"
                autoComplete="new-password"
                value={formValues.newPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <ComoInput
                id="confirm-new-password"
                label={t("profile.confirmNewPassword")}
                name="confirmNewPassword"
                type="password"
                autoComplete="new-password"
                value={formValues.confirmNewPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={handleSaveChanges}
                  disabled={loading}
                  sx={profileButtonSx("outlined")}
                >
                  {t("profile.changePasswordButton")}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
